import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Package, Calendar, Sparkles, Image as ImageIcon,
  CheckCircle, AlertCircle, Loader2, RefreshCw
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { venueApi } from '../../lib/api';
import { resolveAssetUrl, flag } from '../../config';
import { toast } from 'sonner';
import VenueHeader from '../../components/venue/VenueHeader';

const CoasterManagement = () => {
  const { user, loading: authLoading } = useAuth();
  const [venuePartner, setVenuePartner] = useState(null);
  const [location, setLocation] = useState(null);
  const [coasterDesigns, setCoasterDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changingDesign, setChangingDesign] = useState(false);

  useEffect(() => {
    // Wait for AuthContext to resolve before touching user.venue_partner_id.
    // The old one-shot [] effect dereferenced it on first render and crashed
    // the page on any direct load or refresh.
    if (authLoading) return;
    // Access is enforced by <RequireVenue> in App.js; a partner whose profile
    // carries no hub yet simply has nothing to fetch.
    if (!user?.venue_partner_id) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const venueResponse = await venueApi.get(
        `/venue/partner/${user.venue_partner_id}`
      );
      setVenuePartner(venueResponse.data);

      if (venueResponse.data.hub_location_id) {
        const locationResponse = await venueApi.get(
          `/locations/${venueResponse.data.hub_location_id}`
        );
        setLocation(locationResponse.data);
      }

      const designsResponse = await venueApi.get(`/venue/designs`);
      setCoasterDesigns(designsResponse.data);

    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load coaster data');
    } finally {
      setLoading(false);
    }
  };

  const canChangeDesign = () => {
    if (!venuePartner?.last_design_change) return true;
    
    const lastChange = new Date(venuePartner.last_design_change);
    const now = new Date();
    const daysSince = Math.floor((now - lastChange) / (1000 * 60 * 60 * 24));
    
    return daysSince >= 30;
  };

  const handleChangeDesign = async (designId) => {
    if (!canChangeDesign()) {
      toast.error('You can only change your design once per month');
      return;
    }

    setChangingDesign(true);
    try {
      await venueApi.post(
        `/venue/change-design`,
        { design_id: designId }
      );
      
      toast.success('Coaster design updated successfully!');
      fetchData();
    } catch (error) {
      console.error('Failed to change design:', error);
      toast.error(error.response?.data?.detail || 'Failed to change design');
    } finally {
      setChangingDesign(false);
    }
  };

  const getInventoryPercentage = () => {
    if (!location?.initial_coaster_quantity) return 0;
    return (location.coaster_inventory / location.initial_coaster_quantity) * 100;
  };

  const getInventoryStatus = () => {
    const percentage = getInventoryPercentage();
    if (percentage > 50) return { color: 'green', text: 'Good Stock' };
    if (percentage > 20) return { color: 'amber', text: 'Low Stock' };
    return { color: 'red', text: 'Reorder Soon' };
  };

  // V5 Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="text-center">
          <img src="/tappd-logo.png" alt="Tappd" className="h-12 mx-auto mb-6" />
          <Loader2 className="animate-spin mx-auto mb-4" size={48} style={{ color: '#14b8a6' }} />
          <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>Loading coasters...</p>
        </div>
      </div>
    );
  }

  const inventoryStatus = getInventoryStatus();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      {/* V5 Venue Header with Mobile Nav */}
      <VenueHeader user={user} venuePartner={venuePartner} locationData={location} />

      {/* Content */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 pt-24">
        {/* V5 Page Header */}
        <div className="mb-8">
          <p className="font-mono text-xs mb-2" style={{ color: '#14b8a6' }}>
            // coaster_management
          </p>
          <h1 
            className="text-[32px] mb-1"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
          >
            Coaster Management
          </h1>
          <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
            Choose your coaster design and request more coasters
          </p>
        </div>

        {flag('COASTER_INVENTORY') && (<>
        {/* V5 Current Inventory Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 mb-8"
          style={{ 
            borderRadius: '12px',
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #14b8a6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div 
                className="w-14 h-14 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)', borderRadius: '12px' }}
              >
                <Package size={28} style={{ color: '#14b8a6' }} />
              </div>
              <div>
                <h2 
                  className="text-[24px]"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
                >
                  Current Inventory
                </h2>
                <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                  Coasters remaining at your location
                </p>
              </div>
            </div>
            <span 
              className="px-4 py-2 text-[13px] font-semibold"
              style={{ 
                borderRadius: '9999px',
                backgroundColor: inventoryStatus.color === 'green' ? 'rgba(34, 197, 94, 0.1)' :
                                 inventoryStatus.color === 'amber' ? 'rgba(245, 158, 11, 0.1)' :
                                 'rgba(239, 68, 68, 0.1)',
                color: inventoryStatus.color === 'green' ? '#22c55e' :
                       inventoryStatus.color === 'amber' ? '#f59e0b' :
                       '#ef4444'
              }}
            >
              {inventoryStatus.color === 'green' ? <CheckCircle size={16} className="inline mr-2" /> :
               <AlertCircle size={16} className="inline mr-2" />}
              {inventoryStatus.text}
            </span>
          </div>

          <div className="mb-4">
            <div className="flex items-baseline gap-2">
              <span className="text-[56px] font-bold" style={{ color: '#14b8a6' }}>
                {location?.coaster_inventory?.toLocaleString() || 0}
              </span>
              <span className="text-[20px]" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>
                / {location?.initial_coaster_quantity?.toLocaleString() || 0}
              </span>
            </div>
          </div>

          <div 
            className="h-3 overflow-hidden mb-6"
            style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)', borderRadius: '9999px' }}
          >
            <div 
              className="h-full transition-all"
              style={{ 
                width: `${getInventoryPercentage()}%`,
                borderRadius: '9999px',
                backgroundColor: inventoryStatus.color === 'green' ? '#22c55e' :
                                 inventoryStatus.color === 'amber' ? '#f59e0b' :
                                 '#ef4444'
              }}
            />
          </div>
        </motion.div>

        </>)}

        {/* V5 Current Design Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <h2 
            className="text-[24px] mb-4"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
          >
            Current Coaster Design
          </h2>
          <div 
            className="bg-white p-6"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #8b5cf6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            {location?.selected_coaster_id ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <div 
                  className="w-40 h-40 sm:w-48 sm:h-48 flex-shrink-0 flex items-center justify-center overflow-hidden p-4"
                  style={{ 
                    backgroundColor: 'rgba(139, 92, 246, 0.05)',
                    border: '2px solid rgba(139, 92, 246, 0.1)',
                    borderRadius: '12px'
                  }}
                >
                  {coasterDesigns.find(d => d.id === location.selected_coaster_id)?.image_url ? (
                    <img 
                      src={resolveAssetUrl(coasterDesigns.find(d => d.id === location.selected_coaster_id)?.image_url)}
                      alt="Current design"
                      className="max-w-full max-h-full object-contain"
                      style={{ borderRadius: '8px' }}
                    />
                  ) : (
                    <ImageIcon size={48} style={{ color: 'rgba(30, 27, 75, 0.2)' }} />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-[20px] font-bold mb-2" style={{ color: '#1e1b4b' }}>
                    {coasterDesigns.find(d => d.id === location.selected_coaster_id)?.name || 'Current Design'}
                  </h3>
                  <p className="text-[14px] mb-4 capitalize" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    {coasterDesigns.find(d => d.id === location.selected_coaster_id)?.season} {coasterDesigns.find(d => d.id === location.selected_coaster_id)?.year}
                  </p>
                  {venuePartner?.last_design_change && (
                    <div className="flex items-center gap-2 text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                      <Calendar size={16} />
                      Last changed: {new Date(venuePartner.last_design_change).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <div 
                  className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)', borderRadius: '12px' }}
                >
                  <ImageIcon size={32} style={{ color: 'rgba(30, 27, 75, 0.3)' }} />
                </div>
                <p className="text-[16px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>No design selected yet</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Phase 1 (PD-14): coaster reorders are an email, not a stored request */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-teal-200 bg-teal-50 px-5 py-4">
          <p className="text-sm text-[#1e1b4b]">Running low on coasters? Email us and we'll send more.</p>
          <a
            href={`mailto:hello@tappd.app?subject=${encodeURIComponent('Coaster reorder request — ' + (location?.name || 'my venue'))}`}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #14b8a6, #0d9488)' }}
          >
            <RefreshCw size={16} />
            Request more coasters
          </a>
        </div>

        {/* V5 Available Designs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 
              className="text-[24px]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
            >
              Change Design
            </h2>
            {!canChangeDesign() && (
              <div 
                className="flex items-center gap-2 px-4 py-2 text-[13px] font-medium"
                style={{ 
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  color: '#f59e0b',
                  borderRadius: '9999px'
                }}
              >
                <AlertCircle size={16} />
                You can change design once per month
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {coasterDesigns.map((design, index) => (
              <motion.div
                key={design.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="bg-white overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                style={{ 
                  borderRadius: '12px',
                  border: location?.selected_coaster_id === design.id 
                    ? '2px solid #14b8a6' 
                    : '1px solid rgba(30, 27, 75, 0.05)',
                  boxShadow: location?.selected_coaster_id === design.id 
                    ? '0 4px 15px rgba(20, 184, 166, 0.2)' 
                    : '0 2px 4px rgba(30, 27, 75, 0.04)'
                }}
              >
                <div
                  className="aspect-square flex items-center justify-center p-6"
                  style={{ backgroundColor: 'rgba(30, 27, 75, 0.02)' }}
                >
                  <img
                    src={resolveAssetUrl(design.image_url)}
                    alt={design.name}
                    className="max-w-full max-h-full object-contain"
                    style={{ borderRadius: '8px' }}
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-[16px] font-bold mb-1" style={{ color: '#1e1b4b' }}>
                    {design.name}
                  </h3>
                  <p className="text-[13px] capitalize mb-4" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                    {design.season} {design.year}
                  </p>
                  
                  {location?.selected_coaster_id === design.id ? (
                    <div className="flex items-center gap-2 text-[14px] font-semibold" style={{ color: '#14b8a6' }}>
                      <CheckCircle size={18} />
                      Current Design
                    </div>
                  ) : (
                    <button
                      onClick={() => handleChangeDesign(design.id)}
                      disabled={!canChangeDesign() || changingDesign}
                      className="w-full py-2.5 text-[14px] font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ 
                        backgroundColor: 'white',
                        border: '2px solid rgba(20, 184, 166, 0.3)',
                        borderRadius: '12px',
                        color: '#14b8a6'
                      }}
                    >
                      {changingDesign ? (
                        <>
                          <Loader2 className="animate-spin" size={16} />
                          Changing...
                        </>
                      ) : (
                        <>
                          <Sparkles size={16} />
                          Select Design
                        </>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CoasterManagement;
